import { useEffect, useState, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { api, formatApiError } from "../../lib/api";
import { Button } from "../ui/button";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "../ui/select";
import { Crown, TrendingUp, TrendingDown, Star, Trophy } from "lucide-react";

export default function CeoOverviewTab() {
  const { t } = useTranslation();
  const [period, setPeriod] = useState("90");
  const [groupId, setGroupId] = useState("");
  const [groups, setGroups] = useState([]);
  const [data, setData] = useState(null);

  useEffect(() => { (async () => { try { const r = await api.get("/crm/ceo-groups"); setGroups(r.data.groups || []); } catch { /* */ } })(); }, []);

  const load = useCallback(async () => {
    try {
      const r = await api.get("/crm/ceo-overview", { params: { period, group_id: groupId } });
      setData(r.data);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  }, [period, groupId]);
  useEffect(() => { load(); }, [load]);

  const PERIODS = [["30", t("crm.ceo.p30")], ["90", t("crm.ceo.p90")], ["365", t("crm.ceo.p365")], ["all", t("crm.ceo.pAll")]];
  const cols = [
    ["group_name", t("crm.ceo.group")], ["residences", t("crm.ceo.residences")], ["active_partners", t("crm.ceo.activePartners")],
    ["campaigns", t("crm.ceo.campaigns")], ["clicks", t("crm.ceo.clicks")], ["orientations", t("crm.ceo.orientations")],
    ["admissions", t("crm.ceo.admissions")], ["conversion", t("crm.ceo.conversion")], ["satisfaction", t("crm.ceo.satisfaction")],
  ];
  const rows = data ? [...data.groups, data.totals] : [];

  return (
    <div className="space-y-4" data-testid="crm-ceo-tab">
      <div className="flex flex-wrap items-center gap-3">
        <h3 className="font-display text-lg font-semibold flex items-center gap-2"><Crown className="h-4 w-4 text-amber-400" /> {t("crm.ceo.title")}</h3>
        <div className="ml-auto flex gap-2">
          {groups.length > 1 && (
            <Select value={groupId || "all"} onValueChange={(v) => setGroupId(v === "all" ? "" : v)}>
              <SelectTrigger className="w-auto min-w-[150px]" data-testid="ceo-filter-group"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="all">{t("crm.ceo.allGroups")}</SelectItem>{groups.map((g) => <SelectItem key={g.id} value={g.id}>{g.name}</SelectItem>)}</SelectContent>
            </Select>
          )}
          <Select value={period} onValueChange={setPeriod}>
            <SelectTrigger className="w-auto min-w-[130px]" data-testid="ceo-filter-period"><SelectValue /></SelectTrigger>
            <SelectContent>{PERIODS.map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}</SelectContent>
          </Select>
        </div>
      </div>

      {!data ? <p className="text-muted-foreground text-sm py-8">{t("crm.ceo.loading")}</p> : (
        <>
          {/* Podium */}
          {data.groups.length > 0 && (
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3" data-testid="ceo-podium">
              {data.groups.slice(0, 3).map((g, i) => (
                <div key={g.group_id} className="rounded-2xl border border-white/10 bg-white/[0.02] p-4 flex items-center gap-3" style={{ borderColor: [ "rgba(251,191,36,0.4)", "rgba(203,213,225,0.3)", "rgba(217,119,6,0.3)"][i] }}>
                  <Trophy className="h-6 w-6 shrink-0" style={{ color: ["#fbbf24", "#cbd5e1", "#d97706"][i] }} />
                  <div className="min-w-0">
                    <div className="font-medium truncate">{g.group_name}</div>
                    <div className="text-[11px] text-muted-foreground">{g.admissions} {t("crm.ceo.admissions")} · {g.conversion}% · {g.active_partners} {t("crm.ceo.activeShort")}</div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Tableau comparatif */}
          <div className="rounded-2xl border border-white/10 bg-white/[0.02] overflow-x-auto" data-testid="ceo-comparison">
            <table className="w-full text-sm min-w-[820px]">
              <thead>
                <tr className="text-left text-[11px] uppercase tracking-wider text-muted-foreground border-b border-white/10">
                  <th className="p-3 w-8">#</th>
                  {cols.map(([k, l]) => <th key={k} className={`p-3 ${k === "group_name" ? "" : "text-center"}`}>{l}</th>)}
                  <th className="p-3 text-center">{t("crm.ceo.evolution")}</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r, idx) => {
                  const isTotal = idx === rows.length - 1;
                  return (
                    <tr key={r.group_id || "total"} className={`border-b border-white/5 ${isTotal ? "font-semibold bg-amber-500/[0.06]" : "hover:bg-white/[0.02]"}`} data-testid={`ceo-row-${isTotal ? "total" : r.group_id}`}>
                      <td className="p-3 text-muted-foreground">{isTotal ? "" : r.rank}</td>
                      {cols.map(([k]) => (
                        <td key={k} className={`p-3 ${k === "group_name" ? "" : "text-center"}`}>
                          {k === "group_name" ? (isTotal ? t("crm.ceo.total") : r[k])
                            : k === "conversion" ? `${r[k]}%`
                            : k === "satisfaction" ? (r[k] ? <span className="inline-flex items-center gap-0.5 text-amber-400">{r[k]}<Star className="h-3 w-3 fill-amber-400" /></span> : "—")
                            : k === "admissions" ? <span className="text-emerald-400">{r[k]}</span>
                            : k === "clicks" ? <span className="text-violet-300">{r[k]}</span>
                            : r[k]}
                        </td>
                      ))}
                      <td className="p-3 text-center">
                        {isTotal ? "" : (r.evolution >= 0
                          ? <span className="inline-flex items-center gap-0.5 text-emerald-400"><TrendingUp className="h-3 w-3" />+{r.evolution}%</span>
                          : <span className="inline-flex items-center gap-0.5 text-rose-400"><TrendingDown className="h-3 w-3" />{r.evolution}%</span>)}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}
